import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import { Visibility } from '../../components/global/Visibility';
import { PdpRespSimilarItem } from './PdpRespSimilarItem/PdpRespSimilarItem';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';
import { FormattedMessage } from 'react-intl';
import { Button } from '../../global/Button/Button';
import { moreFromThisSeller } from '../pdpMessages';

// actions
import { hasFiredSellerItemsImpressions } from '../../actions/pdpRespSimilarItemsActions';

// styles
import styles from './PdpRespSimilarItemsGridSeller.scss';

// helpers
import get from 'lodash.get';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import {
    trackSellerItemClick,
    trackSellerPageClick,
    trackSellerItemImpressions,
} from '../tracking/similarItemsTracking';

export class PdpRespSimilarItemsGridSellerComponent extends React.Component {
    constructor(props) {
        super(props);

        this.onClickItem = this.onClickItem.bind(this);
        this.onContentVisible = this.onContentVisible.bind(this);

        this.state = {
            contentIsVisible: false,
        };
    }
    componentDidUpdate() {
        if (
            get(this.props, 'sellerItems.edges[0].node.ecommerceTrackingParams') &&
            this.state.contentIsVisible &&
            !this.props.similarItemsState.hasFiredImpressions
        ) {
            trackSellerItemImpressions(this.getGridSellerItems());
            this.props.updateHasFiredImpressions();
        }
    }
    onContentVisible() {
        this.setState({ contentIsVisible: true });
    }
    onClickItem(index) {
        trackSellerItemClick(index, this.getGridSellerItems()[index]);
    }
    getGridSellerItems() {
        return get(this.props, 'sellerItems.edges') || [];
    }
    render() {
        const gridSellerItems = this.getGridSellerItems();

        if (!gridSellerItems.length) {
            return null;
        }

        const { currency, user, viewer } = this.props;
        const { contentIsVisible } = this.state;
        const { isClient } = this.props.relayVariables;
        const favoritesProviderProps = {
            location: locations.SELLER_GRID,
            disable: !contentIsVisible || !isClient,
            userId: user && user.serviceId ? user.serviceId : null,
            environment: this.props.relay.environment,
            itemIds: gridSellerItems.map(({ node }) => node.serviceId),
        };
        const itemPk = get(this.props, 'item.serviceId');
        const sellerPk = get(this.props, 'item.seller.serviceId');

        return (
            <PdpRespSimilarItemsContainer header={moreFromThisSeller} dataTn="pdp-dealer-grid">
                <Visibility onContentVisible={this.onContentVisible} />

                {/* Grid with favoriting and quick view */}
                <FavoritesProvider {...favoritesProviderProps}>
                    {/* favorites  - passed to FavoritesItem */}
                    {({ props: favorites }) => (
                        <QuickViewProvider>
                            {/* quickView - passed to PdpRespSimilarItemQuickView in PdpRespSimilarItem */}
                            {({ quickView }) => (
                                <div className="rowFlex">
                                    {/* Grid of "More From This Seller" items */}
                                    {gridSellerItems.map(({ node }, index) => {
                                        const itemProps = {
                                            isClient,
                                            quickView,
                                            favorites,
                                            onClick: this.onClickItem,
                                            user,
                                        };

                                        return (
                                            <div
                                                key={node.serviceId}
                                                className="colLg3 colMd6 colXs6"
                                            >
                                                <PdpRespSimilarItem
                                                    item={node}
                                                    index={index}
                                                    viewer={viewer}
                                                    currency={currency}
                                                    {...itemProps}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>

                <div className={styles.viewAllWrapper}>
                    <Button
                        onClick={trackSellerPageClick}
                        size="medium"
                        isSecondary
                        href={`/more-from-seller/?itemPk=${itemPk}&sellerPk=${sellerPk}`}
                        dataTn="view-more-from-seller-button"
                    >
                        <FormattedMessage
                            id="PdpRespSimilarItemsGridSeller.view_all"
                            defaultMessage="View All from Seller"
                        />
                    </Button>
                </div>
            </PdpRespSimilarItemsContainer>
        );
    }
}

PdpRespSimilarItemsGridSellerComponent.propTypes = {
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        isClient: PropTypes.bool.isRequired,
    }).isRequired,
    currency: PropTypes.string,
    sellerItems: PropTypes.object,
    item: PropTypes.object,
    relay: PropTypes.shape({
        environment: PropTypes.object.isRequired,
    }).isRequired,
    user: PropTypes.object,

    // redux
    similarItemsState: PropTypes.shape({
        hasFiredImpressions: PropTypes.bool.isRequired,
    }).isRequired,
    updateHasFiredImpressions: PropTypes.func.isRequired,
    viewer: PropTypes.object,
};

// Redux Map Functions
function mapStateToProps({ similarItems }) {
    return {
        similarItemsState: {
            hasFiredImpressions: similarItems.ecommerceImpressions.hasFiredSellerItems,
        },
    };
}
function mapDispatchToProps(dispatch) {
    return {
        updateHasFiredImpressions: () => dispatch(hasFiredSellerItemsImpressions()),
    };
}

export const PdpRespSimilarItemsGridSeller = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(PdpRespSimilarItemsGridSellerComponent),
    {
        viewer: graphql`
            fragment PdpRespSimilarItemsGridSeller_viewer on Viewer {
                ...PdpRespSimilarItem_viewer
            }
        `,
        item: graphql`
            fragment PdpRespSimilarItemsGridSeller_item on Item {
                serviceId
                seller {
                    serviceId
                }
            }
        `,
        sellerItems: graphql`
            fragment PdpRespSimilarItemsGridSeller_sellerItems on moreLikeThisConnection {
                edges {
                    node {
                        serviceId
                        categoryPath
                        categoryCode
                        browseUrl
                        title
                        ecommerceTrackingParams
                        ...PdpRespSimilarItem_item
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpRespSimilarItemsGridSeller_user on User {
                serviceId
                ...PdpRespSimilarItem_user
            }
        `,
    }
);
